import React from 'react';
import { Typography, Button, List, Card, Spin } from 'antd';
import { ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatDuration } from './helpers';
import {
  TimeSelectionPanel as TimeSelectionPanelWrapper,
  TimeSelectionHeader,
  TimeSelectionContent,
} from './styled';

const { Title, Text } = Typography;

const TimeSlotPanel = ({
  selectedDate,
  selectedTimeSlot,
  availableSlots,
  loading,
  monthCapMessage,
  eventType,
  onTimeSlotClick,
  onNextClick,
}) => {
  return (
    <TimeSelectionPanelWrapper>
      <TimeSelectionHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <ClockCircleOutlined style={{ fontSize: '16px', color: eventType.color }} />
          <Title level={4} style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            {selectedDate ? selectedDate.format('MMMM D, YYYY') : 'Available Times'}
          </Title>
        </div>
        {selectedDate ? (
          <Text style={{ fontSize: '13px', color: '#666', marginTop: '2px', display: 'block' }}>
            Choose your preferred time slot below
          </Text>
        ) : (
          <Text style={{ fontSize: '13px', color: '#999', marginTop: '2px', display: 'block' }}>
            Select a date to view available times
          </Text>
        )}
      </TimeSelectionHeader>
      
      <TimeSelectionContent>
        {!selectedDate ? (
          <div className="no-date-placeholder" style={{ textAlign: 'center', padding: '40px 16px', color: '#999' }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              background: '#f5f5f5', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: '2px dashed #e0e0e0'
            }}>
              <CalendarOutlined style={{ fontSize: '28px', color: '#bbb' }} />
            </div>
            <Title level={5} style={{ color: '#666', marginBottom: '6px', fontSize: '15px' }}>
              Select a Date
            </Title>
            <Text style={{ fontSize: '13px', color: '#999' }}>
              Choose a date from the calendar to see available time slots
            </Text>
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            <Spin size="default" />
            <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
              Loading available times...
            </div>
          </div>
        ) : availableSlots.length > 0 ? (
          <List
            dataSource={availableSlots}
            renderItem={(slot) => {
              const isSelected = selectedTimeSlot && 
                moment(selectedTimeSlot.startTime).isSame(moment(slot.startTime));
              
              return (
                <List.Item className="time-slot-item" style={{ padding: '0 0 8px 0', border: 'none' }}>
                  <Card
                    onClick={() => onTimeSlotClick(slot)}
                    className="time-slot-card"
                    style={{ 
                      width: '100%',
                      textAlign: 'center',
                      border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      background: isSelected ? '#f0f8ff' : 'white',
                      boxShadow: isSelected ? '0 2px 8px rgba(24, 144, 255, 0.2)' : 'none'
                    }}
                    bodyStyle={{ padding: '14px 12px' }}
                    hoverable
                  >
                    <div className="time-slot-time" style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      marginBottom: '2px', 
                      color: isSelected ? '#1890ff' : '#262626' 
                    }}>
                      {moment(slot.startTime).format('h:mm A')} CST
                    </div>
                    <div className="time-slot-duration" style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {formatDuration(eventType.duration)}
                    </div>
                  </Card>
                </List.Item>
              );
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            {monthCapMessage ? (
              <>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  background: '#fff7e6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: '2px solid #ffd591'
                }}>
                  <CalendarOutlined style={{ fontSize: '28px', color: '#fa8c16' }} />
                </div>
                <Title level={5} style={{ color: '#d46b08', marginBottom: '8px', fontSize: '15px' }}>
                  Monthly Limit Reached
                </Title>
                <Text style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '12px' }}>
                  {monthCapMessage.message}
                </Text>
                <Text style={{ fontSize: '12px', color: '#999' }}>
                  Try selecting a date in a different month.
                </Text>
              </>
            ) : (
              <>
                <ClockCircleOutlined style={{ fontSize: '36px', color: '#d9d9d9', marginBottom: '12px', display: 'block' }} />
                <Title level={5} style={{ color: '#666', marginBottom: '6px', fontSize: '15px' }}>
                  No Available Times
                </Title>
                <Text style={{ fontSize: '13px', color: '#999' }}>
                  No time slots available for this date.
                  <br />
                  Please select another date.
                </Text>
              </>
            )}
          </div>
        )}
      </TimeSelectionContent>
      
      {/* Next Button */}
      <div style={{ padding: '12px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
        <Button 
          type="primary" 
          size="large"
          onClick={onNextClick}
          disabled={!selectedDate || !selectedTimeSlot}
          block
          style={{ 
            borderRadius: '8px',
            height: '44px',
            fontSize: '15px',
            fontWeight: '500'
          }}
        >
          Next: Your Information
        </Button>
      </div>
    </TimeSelectionPanelWrapper>
  );
};

export default TimeSlotPanel;
